/**
 * Student Lectures Page Component
 * Displays the timetable of scheduled lectures for the student based on enrolled module runs
 */

import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  inject,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { ContactService } from '../../core/services/contact.service';
import {
  StudentLectureService,
  StudentLecture,
} from '../../core/services/student-lecture.service';
import { TranslatePipe, I18nStore } from '../../core/features/translations';
import { PortalUiService } from '../../services/portal-ui.service';

@Component({
  selector: 'app-student-lectures',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
  ],
  template: `
    <div class="p-4 md:p-8 max-w-[1300px] mx-auto animate-fade-in font-sans">


      <!-- Filter / View Controls -->
      <div class="mb-6 flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-xl border border-gray-200/80 shadow-sm">
        <div class="flex items-center gap-2">
          <button
            type="button"
            (click)="timeFilter.set('upcoming')"
            class="px-4 py-2 rounded-lg text-sm font-bold transition-all cursor-pointer"
            [class.bg-blue-600]="timeFilter() === 'upcoming'"
            [class.text-white]="timeFilter() === 'upcoming'"
            [class.bg-gray-100]="timeFilter() !== 'upcoming'"
            [class.text-gray-700]="timeFilter() !== 'upcoming'"
            [class.hover:bg-gray-200]="timeFilter() !== 'upcoming'"
          >
            {{ 'portal.degrees.modules.upcoming' | translate }} ({{ upcomingCount() }})
          </button>
          <button
            type="button"
            (click)="timeFilter.set('past')"
            class="px-4 py-2 rounded-lg text-sm font-bold transition-all cursor-pointer"
            [class.bg-blue-600]="timeFilter() === 'past'"
            [class.text-white]="timeFilter() === 'past'"
            [class.bg-gray-100]="timeFilter() !== 'past'"
            [class.text-gray-700]="timeFilter() !== 'past'"
            [class.hover:bg-gray-200]="timeFilter() !== 'past'"
          >
            {{ 'portal.degrees.modules.past' | translate }} ({{ pastCount() }})
          </button>
          <button
            type="button"
            (click)="timeFilter.set('all')"
            class="px-4 py-2 rounded-lg text-sm font-bold transition-all cursor-pointer"
            [class.bg-blue-600]="timeFilter() === 'all'"
            [class.text-white]="timeFilter() === 'all'"
            [class.bg-gray-100]="timeFilter() !== 'all'"
            [class.text-gray-700]="timeFilter() !== 'all'"
            [class.hover:bg-gray-200]="timeFilter() !== 'all'"
          >
            {{ 'common.all' | translate }} ({{ lectures().length }})
          </button>
        </div>

        <div class="relative min-w-[240px] flex-1 md:flex-initial">
          <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">search</span>
          <input
            type="text"
            [(ngModel)]="searchQuery"
            placeholder="Modul, Dozent, Raum suchen..."
            class="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
          />
        </div>
      </div>

      <!-- Loading State -->
      @if (loading()) {
        <div class="flex flex-col items-center justify-center p-16 text-center gap-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div class="w-12 h-12 border-4 border-gray-100 border-t-blue-600 rounded-full animate-spin"></div>
          <p class="text-gray-600 text-base m-0">{{ 'portal.degrees.loading' | translate }}</p>
        </div>
      } @else if (error()) {
        <div class="flex flex-col items-center justify-center p-16 text-center gap-4 bg-white rounded-2xl border border-red-100 shadow-sm">
          <span class="material-symbols-outlined text-red-500 text-5xl">error</span>
          <p class="text-gray-700 text-base m-0 font-semibold">{{ error() }}</p>
          <button 
            type="button"
            (click)="loadLectures()"
            class="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium shadow-sm hover:bg-blue-700 transition-all focus:outline-none"
          >
            <span class="material-symbols-outlined text-lg">refresh</span>
            {{ 'portal.degrees.retry' | translate }}
          </button>
        </div>
      } @else if (filteredLectures().length === 0) {
        <div class="flex flex-col items-center justify-center p-16 text-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
          <span class="material-symbols-outlined text-gray-300 text-6xl mb-4">event_busy</span>
          <h3 class="text-gray-800 text-lg font-bold m-0 mb-1">{{ 'common.noResults' | translate }}</h3>
          <p class="text-gray-500 text-sm m-0">Keine Vorlesungen für diese Auswahl gefunden.</p>
        </div>
      } @else {
        <!-- Lectures Table / List -->
        <div class="w-full overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm custom-scrollbar">
          <table class="w-full border-collapse text-sm text-left table-fixed">
            <thead>
              <tr class="bg-gray-50/80 border-b border-gray-200">
                <th class="px-4 py-3.5 font-semibold text-gray-600 uppercase tracking-wider text-[0.7rem] w-[22%]">Datum & Zeit</th>
                <th class="px-4 py-3.5 font-semibold text-gray-600 uppercase tracking-wider text-[0.7rem] w-[32%]">{{ 'portal.degrees.modules.table.name' | translate }}</th>
                <th class="px-4 py-3.5 font-semibold text-gray-600 uppercase tracking-wider text-[0.7rem] w-[24%]">{{ 'portal.degrees.modules.table.location' | translate }}</th>
                <th class="px-4 py-3.5 font-semibold text-gray-600 uppercase tracking-wider text-[0.7rem] w-[22%]">Dozierende</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              @for (lecture of filteredLectures(); track lecture.lecture_block_id) {
                <tr class="hover:bg-blue-50/30 transition-all">
                  <!-- Date & Time -->
                  <td class="px-4 py-4 align-top">
                    <div class="flex flex-col gap-1">
                      <div class="flex items-center gap-2 text-gray-900 font-bold text-[0.95rem]">
                        <span class="material-symbols-outlined text-blue-600 text-base">calendar_today</span>
                        <span>{{ formatDate(lecture.reservation_date) }}</span>
                      </div>
                      <div class="flex items-center gap-1.5 text-xs text-gray-600 font-medium pl-6">
                        <span class="material-symbols-outlined text-gray-400 text-[0.85rem]">schedule</span>
                        <span>{{ formatTime(lecture.reservation_time_from) }} - {{ formatTime(lecture.reservation_time_to) }}</span>
                      </div>
                    </div>
                  </td>

                  <!-- Module & Run -->
                  <td class="px-4 py-4 align-top">
                    <div class="flex flex-col gap-1">
                      <div class="flex flex-wrap items-center gap-2">
                        <span class="font-bold text-gray-900 text-[0.95rem] leading-snug">{{ lecture.module_name }}</span>
                        @if (lecture.variant_code) {
                          <span class="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[0.7rem] font-bold border border-blue-100">{{ lecture.variant_code }}</span>
                        }
                      </div>
                      @if (lecture.module_run_name) {
                        <span class="text-xs text-gray-500 font-medium">{{ lecture.module_run_name }}</span>
                      }
                      @if (lecture.degree_code) {
                        <span class="text-xs text-purple-600 font-medium">{{ lecture.degree_code }}</span>
                      }
                    </div>
                  </td>

                  <!-- Location & Room -->
                  <td class="px-4 py-4 align-top">
                    <div class="flex flex-col gap-1">
                      <div class="flex items-center gap-1.5 text-gray-800 font-medium">
                        <span class="material-symbols-outlined text-red-500 text-base">location_on</span>
                        <span>{{ lecture.location_shortname || lecture.location_name || '-' }}</span>
                      </div>
                      @if (lecture.resources && lecture.resources.length > 0) {
                        <div class="flex items-center gap-1.5 text-xs text-gray-600 pl-5">
                          <span class="material-symbols-outlined text-gray-400 text-sm">meeting_room</span>
                          <span>{{ formatResources(lecture.resources) }}</span>
                        </div>
                      }
                    </div>
                  </td>

                  <!-- Teachers -->
                  <td class="px-4 py-4 align-top">
                    <div class="flex flex-col gap-1">
                      @if (lecture.teachers && lecture.teachers.length > 0) {
                        @for (t of lecture.teachers; track t.id) {
                          <div class="flex items-center gap-1.5 text-gray-800 font-medium">
                            <span class="material-symbols-outlined text-blue-500 text-base">person</span>
                            <span>{{ t.name }}</span>
                          </div>
                        }
                      } @else {
                        <span class="text-gray-400 italic">-</span>
                      }
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StudentLecturesComponent implements OnInit {
  private readonly studentLectureService = inject(StudentLectureService);
  private readonly contactService = inject(ContactService);
  private readonly authService = inject(AuthService);
  private readonly uiService = inject(PortalUiService);
  private readonly i18n = inject(I18nStore);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly lectures = signal<StudentLecture[]>([]);
  readonly timeFilter = signal<'upcoming' | 'past' | 'all'>('upcoming');
  searchQuery = '';

  readonly upcomingCount = computed(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return this.lectures().filter((l) => {
      if (!l.reservation_date) return true;
      return new Date(l.reservation_date) >= today;
    }).length;
  });

  readonly pastCount = computed(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return this.lectures().filter((l) => {
      if (!l.reservation_date) return false;
      return new Date(l.reservation_date) < today;
    }).length;
  });

  readonly filteredLectures = computed(() => {
    let list = this.lectures();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const filter = this.timeFilter();
    if (filter === 'upcoming') {
      list = list.filter((l) => !l.reservation_date || new Date(l.reservation_date) >= today);
    } else if (filter === 'past') {
      list = list.filter((l) => l.reservation_date && new Date(l.reservation_date) < today);
    }

    const q = this.searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((l) => {
        const mod = (l.module_name || '').toLowerCase();
        const run = (l.module_run_name || '').toLowerCase();
        const loc = (l.location_name || l.location_shortname || '').toLowerCase();
        const teach = (l.teachers || []).some((t) => (t.name || '').toLowerCase().includes(q));
        const room = (l.resources || []).some(
          (r) => (r.name || '').toLowerCase().includes(q) || (r.code || '').toLowerCase().includes(q)
        );
        return mod.includes(q) || run.includes(q) || loc.includes(q) || teach || room;
      });
    }

    return list;
  });

  ngOnInit(): void {
    this.loadLectures();
  }

  async loadLectures(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const userInfo = this.authService.getUserInfo();
      const userEmail = userInfo?.sub || userInfo?.email;

      if (!userEmail) {
        throw new Error('User email not found');
      }

      const contact = await this.contactService.getContactByEmail(userEmail).toPromise();

      if (!contact || !contact.contact_id) {
        throw new Error('Contact not found for user');
      }

      const data = await this.studentLectureService
        .getStudentLectures(contact.contact_id)
        .toPromise();

      const lectureList = data || [];
      this.lectures.set(lectureList);
      this.uiService.setPageCount(lectureList.length);

      // Dynamically highlight student's active school(s) in header banner
      const activeSchools = Array.from(
        new Set(
          lectureList
            .map((l) => l.school_shortname)
            .filter((s): s is string => !!s && s.trim().length > 0)
        )
      );
      if (activeSchools.length > 0) {
        this.uiService.setActiveSchools(activeSchools);
      }

      this.loading.set(false);
    } catch (error) {
      console.error('❌ Error loading student lectures:', error);
      this.error.set(
        error instanceof Error ? error.message : this.i18n.t('portal.degrees.error.loading')
      );
      this.loading.set(false);
    }
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    try {
      const d = new Date(dateString);
      return d.toLocaleDateString('de-DE', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  }

  formatTime(timeString: string | null | undefined): string {
    if (!timeString) return '-';
    return timeString.substring(0, 5);
  }

  formatResources(resources: any[]): string {
    if (!resources || resources.length === 0) return '-';
    return resources.map((r) => r.code || r.name).join(', ');
  }
}
